import React, { FC, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/system";
import { useRecoilState, useRecoilValue } from "recoil";
import { COLORS, FONT_FAMILIES, SPACINGS } from "../../common/consts";
import { organizationAtom, signedInUserAtom } from "../../state/atoms";
import { ProfileType } from "../../types/users";
import type { JobChangeSettings as JobChangeSettingsValue, Organization } from "../../types/organizations";
import { getHubSpotLeadStatuses, getOutreachProspectStages, updateJobChangeSettings } from "../../services/firebase";
import { useIsHubSpotCrm } from "../CrmUi/useCrm";
import Toggle from "../DataInput/Toggle";
import Select from "../DataInput/Select";
import Button, { ButtonVariants } from "../Display/Button";
import { BlockBone } from "../Display/Skeletons";
import { SettingsCaption, SettingsControlField, SettingsFooter, SettingsGroup, SettingsHeader, SettingsPanel, SettingsRow } from "./index";

/* "When someone changes jobs": two choices, each a radio with its own explanation. */
const Choices = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${SPACINGS.md};
  padding: 0 0 ${SPACINGS.md};
  max-width: 640px;
`;

const Choice = styled("label")<{ disabled?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${SPACINGS.sm};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  font-family: ${FONT_FAMILIES.regular};

  input {
    margin: 2px 0 0;
    accent-color: var(--color-toggleActive, ${COLORS.toggleActive});
    flex: 0 0 auto;
  }
`;

const ChoiceText = styled("span")`
  display: block;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});

  small {
    display: block;
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
    margin-top: 2px;
  }
`;

const Note = styled("p")`
  margin: ${SPACINGS.sm} 0 0;
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
`;

const AdminNote = styled("p")`
  margin: 0 0 ${SPACINGS.sm};
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
`;

const DEFAULTS: JobChangeSettingsValue = { mode: "updateRecord", companyCreation: "create", closeOutStatus: null, closeOutStatusLabel: null, createWithoutCompany: false, autoApply: false, ownerTask: false };

const settingsOf = (org: Organization | null): JobChangeSettingsValue => {
  const raw: Partial<JobChangeSettingsValue> = org?.jobChange || {};
  return {
    mode: raw.mode === "closeAndCreate" ? "closeAndCreate" : "updateRecord",
    companyCreation: raw.companyCreation === "existingOnly" ? "existingOnly" : "create",
    closeOutStatus: raw.closeOutStatus || null,
    closeOutStatusLabel: raw.closeOutStatusLabel || null,
    createWithoutCompany: raw.companyCreation === "existingOnly" && raw.createWithoutCompany === true,
    autoApply: raw.autoApply === true,
    ownerTask: raw.ownerTask === true,
  };
};

interface StatusOption {
  value: string;
  label: string;
}

/**
 * Settings > Configuration > Job changes. Organization-wide and admin only
 * (updateOrgSettings refuses anyone else); everyone can see what is set,
 * since it decides what the extension's and the HubSpot card's job change
 * dialog offers.
 */
const JobChangeSettings: FC = () => {
  const [organization, setOrganization] = useRecoilState(organizationAtom);
  const user = useRecoilValue(signedInUserAtom);
  const isHubSpot = useIsHubSpotCrm();
  const words = isHubSpot
    ? { crm: "HubSpot", record: "contact", records: "contacts", company: "company", status: "lead status", statusTitle: "Close-out lead status" }
    : { crm: "Outreach", record: "prospect", records: "prospects", company: "account", status: "stage", statusTitle: "Close-out stage" };
  // A DEFAULT profile is a regular user; admins, leadership and accounts from before roles manage settings.
  const isAdmin = user?.profile !== ProfileType.DEFAULT;

  const saved = useMemo(() => settingsOf(organization), [organization]);
  const [value, setValue] = useState<JobChangeSettingsValue>(saved);
  const [options, setOptions] = useState<StatusOption[] | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => setValue(saved), [saved]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (isHubSpot) {
          const { result } = await getHubSpotLeadStatuses();
          if (alive) setOptions((result || []).map((s) => ({ value: s.value, label: s.label })));
        } else {
          const { stages, error } = await getOutreachProspectStages();
          if (!alive) return;
          setOptions(stages.map((s) => ({ value: s.id, label: s.name })));
          setOptionsError(error);
        }
      } catch (e: any) {
        if (!alive) return;
        setOptions([]);
        setOptionsError(e?.message || `Could not read your ${words.crm} ${words.status} options.`);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isHubSpot, words.crm, words.status]);

  const existingOnly = value.companyCreation === "existingOnly";
  const needsStatus = value.mode === "closeAndCreate" || (existingOnly && !value.createWithoutCompany);
  const missingStatus = needsStatus && !value.closeOutStatus;
  const dirty =
    value.mode !== saved.mode ||
    value.companyCreation !== saved.companyCreation ||
    (value.closeOutStatus || null) !== (saved.closeOutStatus || null) ||
    !!value.createWithoutCompany !== !!saved.createWithoutCompany ||
    !!value.autoApply !== !!saved.autoApply ||
    !!value.ownerTask !== !!saved.ownerTask;

  const patch = (next: Partial<JobChangeSettingsValue>) => {
    setValue((v) => ({ ...v, ...next }));
    setMessage(null);
  };

  const save = async () => {
    if (missingStatus) {
      setMessage(`Pick a ${words.status} to close records out with before saving.`);
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const label = options?.find((o) => o.value === value.closeOutStatus)?.label || value.closeOutStatusLabel || null;
      const next = {
        mode: value.mode,
        companyCreation: value.companyCreation,
        closeOutStatus: value.closeOutStatus || null,
        closeOutStatusLabel: value.closeOutStatus ? label : null,
        createWithoutCompany: existingOnly && !!value.createWithoutCompany,
        autoApply: !!value.autoApply,
        ownerTask: !!value.ownerTask,
      };
      await updateJobChangeSettings(next);
      setOrganization((org) => (org ? ({ ...org, jobChange: { ...next, updatedAt: Date.now() } } as Organization) : org));
      setMessage("Saved.");
    } catch (e: any) {
      setMessage(e?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const statusOptions = [
    { value: "", label: `Select a ${words.status}` },
    ...(options || []),
    // A saved value the CRM no longer lists still shows, so it is not silently cleared.
    ...(value.closeOutStatus && options && !options.some((o) => o.value === value.closeOutStatus)
      ? [{ value: value.closeOutStatus, label: value.closeOutStatusLabel || value.closeOutStatus }]
      : []),
  ];

  return (
    <SettingsPanel id="job-change-settings">
      <SettingsHeader
        title="Job changes"
        caption={`What boring does when a ${words.record} changes jobs, and whether boring may add new ${words.company === "company" ? "companies" : "accounts"} to ${words.crm}. Every job change boring finds is on the Job changes page, where reps approve or reject their own and admins the team's.`}
      />
      {!isAdmin && <AdminNote>Only admins can change this.</AdminNote>}

      <SettingsGroup>
        {/* The choices sit under the row at full width, not in the row's narrow detail column. */}
        <div>
          <SettingsRow
            label="When someone changes jobs"
            caption={`Applies when a rep confirms a job change from the extension or the ${isHubSpot ? "HubSpot contact card" : "prospect page"}, or when boring applies it automatically (below).`}
          />
          <Choices role="radiogroup" aria-label="When someone changes jobs">
            <Choice disabled={!isAdmin}>
              <input type="radio" name="job-change-mode" value="updateRecord" checked={value.mode === "updateRecord"} disabled={!isAdmin} onChange={() => patch({ mode: "updateRecord" })} />
              <ChoiceText>
                Update their current record
                <small>{`The ${words.record} moves to the new ${words.company} with the new title, and boring looks for their new work email.`}</small>
              </ChoiceText>
            </Choice>
            <Choice disabled={!isAdmin}>
              <input type="radio" name="job-change-mode" value="closeAndCreate" checked={value.mode === "closeAndCreate"} disabled={!isAdmin} onChange={() => patch({ mode: "closeAndCreate" })} />
              <ChoiceText>
                Close out the old record and create a new one
                <small>{`The old ${words.record} keeps its details and gets the close-out ${words.status}. A new ${words.record} is made at the new ${words.company} with the same owner, and the two are linked.`}</small>
              </ChoiceText>
            </Choice>
          </Choices>
        </div>

        <SettingsRow
          label={`Only add people at ${words.company === "company" ? "companies" : "accounts"} that already exist in ${words.crm}`}
          caption={
            value.createWithoutCompany
              ? `Applies to job changes and exports. boring never creates a new ${words.company}: someone at one ${words.crm} doesn't have is added without a ${words.company} (below).`
              : `Applies to job changes and exports. boring never creates a new ${words.company}: a job change to one ${words.crm} doesn't have closes the old record out instead, and an export of someone at one is refused.`
          }
          control={<Toggle checked={existingOnly} disabled={!isAdmin} onChange={(on) => patch({ companyCreation: on ? "existingOnly" : "create", ...(on ? {} : { createWithoutCompany: false }) })} />}
        />

        {existingOnly && (
          <SettingsRow
            label={`Still add people when their ${words.company} isn't in ${words.crm}`}
            caption={`Instead of refusing the export or closing the record out, boring adds the ${words.record} with no ${words.company} association. A job change updates the ${words.record}'s title and company${value.mode === "closeAndCreate" ? `, and the new ${words.record} is made` : ""} without linking a ${words.company}.`}
            control={<Toggle checked={!!value.createWithoutCompany} disabled={!isAdmin} onChange={(on) => patch({ createWithoutCompany: on })} />}
          />
        )}

        <SettingsRow
          label={`Apply job changes automatically when the ${words.company} is already in ${words.crm}`}
          caption={
            value.mode === "closeAndCreate"
              ? `As soon as boring sees the move on LinkedIn (a visit, or the read before a task), it closes out the old ${words.record} and creates the new one, without waiting for a rep. A move to a ${words.company} ${words.crm} doesn't have still waits for a rep on the Job changes page.`
              : `As soon as boring sees the move on LinkedIn (a visit, or the read before a task), it updates the ${words.record} with the new title and ${words.company}, without waiting for a rep. A move to a ${words.company} ${words.crm} doesn't have still waits for a rep on the Job changes page.`
          }
          control={<Toggle checked={!!value.autoApply} disabled={!isAdmin} onChange={(on) => patch({ autoApply: on })} />}
        />

        <SettingsRow
          label={`Give the ${words.record} owner a task to review each job change`}
          caption={`Usually not needed: the Job changes page lists every change for its owner to approve or reject. With this on, boring also adds a to-do in ${words.crm}, assigned to the ${words.record}'s owner: on the new ${words.record} when boring made one, otherwise on the old one.`}
          control={<Toggle checked={!!value.ownerTask} disabled={!isAdmin} onChange={(on) => patch({ ownerTask: on })} />}
        />

        <SettingsRow
          label={words.statusTitle}
          caption={
            needsStatus
              ? `Set on the old ${words.record} when it is closed out. Required with the settings above.`
              : `Used only when records are closed out (either setting above).`
          }
          control={
            <SettingsControlField>
              {options === null ? (
                <BlockBone height={36} />
              ) : (
                <Select
                  fullWidth
                  displayEmpty
                  value={value.closeOutStatus || ""}
                  disabled={!isAdmin}
                  onChange={(e: any) => {
                    const v = String(e?.target?.value ?? e ?? "");
                    patch({ closeOutStatus: v || null, closeOutStatusLabel: options.find((o) => o.value === v)?.label || null });
                  }}
                  options={statusOptions}
                />
              )}
            </SettingsControlField>
          }
        />
      </SettingsGroup>
      {optionsError && <Note>{optionsError}</Note>}
      <Note>
        {`Matching by location is always on: when ${words.crm} has several ${words.company === "company" ? "companies" : "accounts"} for the new employer (for example Microsoft US, Microsoft EMEA and Microsoft APAC), boring picks the one in the person's country or region, using the location on the ${words.record} or their LinkedIn profile.`}
      </Note>

      {isAdmin && (
        <SettingsFooter start={(message || missingStatus) && <SettingsCaption>{message || `Pick a ${words.status} to save.`}</SettingsCaption>}>
          {dirty && (
            <Button variant={ButtonVariants.light} type="button" disabled={saving} onClick={() => setValue(saved)}>
              Cancel
            </Button>
          )}
          <Button variant={ButtonVariants.brand} type="button" disabled={!dirty || saving || missingStatus} loading={saving} onClick={save}>
            Save
          </Button>
        </SettingsFooter>
      )}
    </SettingsPanel>
  );
};

export { DEFAULTS as DEFAULT_JOB_CHANGE_SETTINGS };
export default JobChangeSettings;
